import * as React from 'react';

import * as hiveCore from '@gitbook/hive-core';
import { useHiveBranch, useHiveRevision } from '@gitbook/hive-react';
import { createPalette, DropLine, usePaletteHandle } from '@gitbook/react-uikit';
import { Icon } from '@gitbook/spine-icons';
import { assertIsDefined, assertNever } from '@gitbook/util-ts';

import { type DNDItem, useContentNavigation, useDroppableWithCallback } from '../../../Components';

/**
 * Line to insert page in the TOC.
 */
export function ToCInsertionLine(props: {
    page: hiveCore.RevisionPage;
    position: 'after' | 'before';
    visible: boolean;
    canInsertGroup: boolean;
}): React.ReactElement {
    const { page, position, visible, canInsertGroup } = props;

    const { navigateToPage } = useContentNavigation();
    const hiveBranch = useHiveBranch();
    const { sourceRevision } = useHiveRevision();

    // Drop element in the page
    const drop = useDroppableWithCallback({
        shouldAccept: (item) => {
            if (item.type === 'page') {
                return hiveCore.isPageMoveableInRevisionNextTo(sourceRevision, item.id, page.id);
            }

            return false;
        },

        onDrop: (item: DNDItem) => {
            assertIsDefined(hiveBranch);

            if (item.type === 'page') {
                hiveBranch.change((change) => {
                    switch (position) {
                        case 'before': {
                            change.movePageBefore(item.id, page.id);
                            break;
                        }
                        case 'after': {
                            change.movePageAfter(item.id, page.id);
                            break;
                        }
                        default:
                            assertNever(position);
                    }
                });
            }
        },
    });
    const { setNodeRef } = drop;
    const dropLineRef = React.useCallback(
        (node: HTMLDivElement | null) => {
            setNodeRef(node);
            paletteAnchorRef.current = node;
        },
        [setNodeRef]
    );

    // Insert page
    const insertPage = () => {
        assertIsDefined(hiveBranch);

        const newPage = hiveCore.createPageDocument({
            title: hiveCore.getUniquePageTitle(hiveBranch.revision),
        });

        hiveBranch.change((change) => {
            switch (position) {
                case 'before': {
                    change.insertPageBefore(newPage, page.id);
                    break;
                }
                case 'after': {
                    // When inserting "after" a group, in fact we insert as a first child of the group
                    if (page.type === hiveCore.RevisionPageType.Group) {
                        change.insertPage(newPage, {
                            into: page.id,
                            at: 0,
                        });
                    } else {
                        change.insertPageAfter(newPage, page.id);
                    }
                    break;
                }
                default:
                    assertNever(position);
            }
        });

        navigateToPage(hiveBranch.revision, newPage.id);
    };

    // Insert group
    const insertGroup = () => {
        if (!canInsertGroup) {
            return;
        }

        assertIsDefined(hiveBranch);

        const newPage = hiveBranch.syncChange((change) => {
            const pageGroup = hiveCore.createPageGroup({
                title: change.getUniquePageTitle('Group', true),
            });

            const pageDocument = hiveCore.createPageDocument({
                title: change.getUniquePageTitle('Page', true),
            });

            switch (position) {
                case 'before': {
                    change.insertPageBefore(pageGroup, page.id);
                    break;
                }
                case 'after': {
                    if (page.type === hiveCore.RevisionPageType.Group) {
                        change.insertPage(pageGroup, {
                            into: page.id,
                            at: 0,
                        });
                    } else {
                        change.insertPageAfter(pageGroup, page.id);
                    }
                    break;
                }
                default:
                    assertNever(position);
            }

            change.insertPage(pageDocument, {
                into: pageGroup.id,
            });

            return pageDocument;
        });

        navigateToPage(hiveBranch.revision, newPage.id, { preserveSideSheet: true });
    };

    const paletteAnchorRef = React.useRef<HTMLDivElement | null>(null);
    const paletteHandle = usePaletteHandle(paletteAnchorRef);
    const palette = () => {
        if (!canInsertGroup) {
            return null;
        }

        return createPalette({
            groups: [
                {
                    key: 'tocInsertion',
                    commands: [
                        {
                            key: 'tocInsertion.group',
                            icon: Icon.AddPageGroup,
                            title: 'Insert group',
                            trigger: () => {
                                paletteHandle.close();
                                insertGroup();
                            },
                        },
                        {
                            key: 'tocInsertion.page',
                            icon: Icon.Page,
                            title: 'Insert page',
                            trigger: () => {
                                paletteHandle.close();
                                insertPage();
                            },
                        },
                    ],
                },
            ],
        });
    };

    return (
        <DropLine
            ref={dropLineRef}
            dropHovered={drop.isOver}
            buttonVisible={visible}
            position={(() => {
                switch (position) {
                    case 'before':
                        return 'top';
                    case 'after':
                        return 'bottom';
                    default:
                        assertNever(position);
                }
            })()}
            buttonAlign="end"
            insertButton={{
                onPress: () => {
                    const paletteDef = palette();
                    if (paletteDef) {
                        paletteHandle.open(paletteDef);
                    } else {
                        insertPage();
                    }
                },
                label: canInsertGroup ? 'Insert' : 'Insert page',
            }}
        />
    );
}
